/**
 * Global Chat Component with Private Messaging
 * Real-time messaging for all registered users
 * Messages are stored for 48 hours
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

function GlobalChat() {
  const { user, language } = React.useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [privateChats, setPrivateChats] = useState({});
  const [selectedChat, setSelectedChat] = useState('global'); // 'global' or user_id
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [cooldownTime, setCooldownTime] = useState(0);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const cooldownIntervalRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const translations = {
    en: {
      title: 'Chat',
      globalChat: 'Global Chat',
      onlineUsers: 'Online Users',
      loading: 'Loading...',
      error: 'Failed to load messages',
      notLoggedIn: 'Please sign in to use chat',
      placeholder: 'Type your message...',
      send: 'Send',
      tooLong: 'Message is too long (max 500 characters)',
      tooShort: 'Message cannot be empty',
      rateLimited: 'Wait {seconds}s before sending',
      sendError: 'Failed to send message',
      noMessages: 'No messages yet',
      you: 'You',
      anonymous: 'Anonymous',
      chatWith: 'Chat with {user}',
      backToGlobal: '← Global Chat',
      noUsers: 'No users online',
      messageRetention: 'Messages auto-delete after 48h',
      toggleSidebar: '☰'
    },
    ru: {
      title: 'Чат',
      globalChat: 'Общий Чат',
      onlineUsers: 'Онлайн',
      loading: 'Загрузка...',
      error: 'Не удалось загрузить сообщения',
      notLoggedIn: 'Войдите, чтобы использовать чат',
      placeholder: 'Введите сообщение...',
      send: 'Отправить',
      tooLong: 'Слишком длинное (макс. 500)',
      tooShort: 'Сообщение не может быть пустым',
      rateLimited: 'Подождите {seconds} сек',
      sendError: 'Не удалось отправить',
      noMessages: 'Пока нет сообщений',
      you: 'Вы',
      anonymous: 'Аноним',
      chatWith: 'Чат с {user}',
      backToGlobal: '← Общий чат',
      noUsers: 'Никого нет онлайн',
      messageRetention: 'Сообщения удаляются через 48ч',
      toggleSidebar: '☰'
    }
  };

  const t = translations[language] || translations.en;

  // Load messages
  const loadMessages = async () => {
    try {
      if (selectedChat === 'global') {
        const response = await axios.get('/web/global-chat/messages');
        setMessages(response.data.messages || []);
      } else {
        const response = await axios.get(`/web/private-chat/messages/${selectedChat}`);
        setMessages(response.data.messages || []);
      }
      setError('');
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  // Load online users
  const loadOnlineUsers = async () => {
    try {
      const response = await axios.get('/web/global-chat/online-users');
      setOnlineUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to load online users:', err);
    }
  };

  // Initial load
  useEffect(() => {
    if (user) {
      loadMessages();
      loadOnlineUsers();
    } else {
      setLoading(false);
    }
  }, [user, selectedChat]);

  // Poll for new messages every 3 seconds
  useEffect(() => {
    if (user) {
      pollIntervalRef.current = setInterval(() => {
        loadMessages();
        loadOnlineUsers();
      }, 3000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [user, selectedChat]);

  // Cooldown timer
  useEffect(() => {
    if (cooldownTime > 0) {
      cooldownIntervalRef.current = setInterval(() => {
        setCooldownTime(prev => Math.max(0, prev - 1));
      }, 1000);

      return () => {
        if (cooldownIntervalRef.current) {
          clearInterval(cooldownIntervalRef.current);
        }
      };
    }
  }, [cooldownTime]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!user) {
      return;
    }

    const trimmedMessage = newMessage.trim();
    
    if (trimmedMessage.length === 0) {
      setError(t.tooShort);
      return;
    }

    if (trimmedMessage.length > 500) {
      setError(t.tooLong);
      return;
    }

    if (cooldownTime > 0) {
      setError(t.rateLimited.replace('{seconds}', cooldownTime));
      return;
    }

    setSending(true);
    setError('');

    try {
      if (selectedChat === 'global') {
        await axios.post('/web/global-chat/send', { message: trimmedMessage });
        setCooldownTime(100); // 100 seconds cooldown
      } else {
        await axios.post('/web/private-chat/send', { 
          recipient_id: selectedChat,
          message: trimmedMessage 
        });
        setCooldownTime(40); // 40 seconds cooldown
      }
      
      setNewMessage('');
      loadMessages();
    } catch (err) {
      console.error('Failed to send message:', err);
      if (err.response?.status === 429) {
        const remaining = err.response?.data?.wait_seconds || 
                         (selectedChat === 'global' ? 100 : 40);
        setCooldownTime(remaining);
        setError(t.rateLimited.replace('{seconds}', remaining));
      } else {
        setError(t.sendError);
      }
    } finally {
      setSending(false);
    }
  };

  const handleUserClick = (userId) => {
    setSelectedChat(userId);
    setCooldownTime(0);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    
    return date.toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div style={{ 
        padding: '40px 20px', 
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h2 style={{ color: '#fff', marginBottom: '20px' }}>{t.title}</h2>
        <p style={{ color: '#888' }}>{t.notLoggedIn}</p>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      height: 'calc(100vh - 60px)',
      width: '100%',
      backgroundColor: '#0a0a0a'
    }}>
      {/* Main Chat Area */}
      <div style={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0
      }}>
        {/* Chat Header */}
        <div style={{
          padding: '15px 20px',
          borderBottom: '1px solid #222',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#111'
        }}>
          <div>
            {selectedChat === 'global' ? (
              <>
                <h2 style={{ color: '#fff', margin: 0, fontSize: '20px' }}>{t.globalChat}</h2>
                <p style={{ color: '#666', margin: '4px 0 0 0', fontSize: '12px' }}>
                  {t.messageRetention}
                </p>
              </>
            ) : (
              <div>
                <button
                  onClick={() => setSelectedChat('global')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#e91e63',
                    cursor: 'pointer',
                    padding: 0,
                    marginBottom: '5px',
                    fontSize: '14px'
                  }}
                >
                  {t.backToGlobal}
                </button>
                <h2 style={{ color: '#fff', margin: 0, fontSize: '20px' }}>
                  {t.chatWith.replace('{user}', 
                    onlineUsers.find(u => u.id === selectedChat)?.nickname || 
                    onlineUsers.find(u => u.id === selectedChat)?.email || 
                    t.anonymous
                  )}
                </h2>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              background: '#222',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer',
              padding: '8px 12px',
              fontSize: '18px'
            }}
          >
            {t.toggleSidebar}
          </button>
        </div>

        {/* Messages Area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
              {t.loading}
            </div>
          ) : error && messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#e91e63', padding: '20px' }}>
              {error}
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
              {t.noMessages}
            </div>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.user_id === user.id;
              const senderName = isOwn ? t.you : (msg.nickname || msg.email || t.anonymous);
              
              return (
                <div 
                  key={msg.id} 
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}
                >
                  <div style={{
                    maxWidth: '70%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    backgroundColor: isOwn ? '#e91e63' : '#333',
                    color: '#fff'
                  }}>
                    <div style={{ 
                      fontSize: '12px', 
                      color: isOwn ? 'rgba(255,255,255,0.9)' : '#bbb',
                      marginBottom: '4px',
                      fontWeight: '500'
                    }}>
                      {selectedChat === 'global' && (
                        <span
                          onClick={() => !isOwn && handleUserClick(msg.user_id)}
                          style={{
                            cursor: isOwn ? 'default' : 'pointer',
                            textDecoration: isOwn ? 'none' : 'underline',
                            color: isOwn ? 'rgba(255,255,255,0.9)' : '#4caf50'
                          }}
                        >
                          {senderName}
                        </span>
                      )}
                      {selectedChat !== 'global' && senderName}
                    </div>
                    <div style={{ wordWrap: 'break-word' }}>{msg.message}</div>
                    <div style={{ 
                      fontSize: '10px', 
                      color: isOwn ? 'rgba(255,255,255,0.6)' : '#666',
                      marginTop: '4px',
                      textAlign: 'right'
                    }}>
                      {formatTime(msg.created_at)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div style={{
          borderTop: '1px solid #222',
          padding: '15px 20px',
          backgroundColor: '#111'
        }}>
          {error && (
            <div style={{ 
              color: '#e91e63', 
              marginBottom: '10px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}
          <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={t.placeholder}
              disabled={sending || cooldownTime > 0}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: '24px',
                border: '1px solid #333',
                backgroundColor: '#1a1a1a',
                color: '#fff',
                fontSize: '14px',
                outline: 'none'
              }}
              maxLength={500}
            />
            <button
              type="submit"
              disabled={sending || cooldownTime > 0 || !newMessage.trim()}
              style={{
                padding: '12px 24px',
                borderRadius: '24px',
                border: 'none',
                backgroundColor: cooldownTime > 0 ? '#444' : '#e91e63',
                color: '#fff',
                cursor: cooldownTime > 0 || sending ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                minWidth: '100px',
                opacity: (sending || cooldownTime > 0 || !newMessage.trim()) ? 0.5 : 1
              }}
            >
              {cooldownTime > 0 ? `${cooldownTime}s` : sending ? '...' : t.send}
            </button>
          </form>
        </div>
      </div>

      {/* Sidebar - Online Users */}
      {sidebarOpen && (
        <div style={{
          width: '280px',
          borderLeft: '1px solid #222',
          backgroundColor: '#111',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '15px 20px',
            borderBottom: '1px solid #222'
          }}>
            <h3 style={{ color: '#fff', margin: 0, fontSize: '16px' }}>
              {t.onlineUsers} ({onlineUsers.length})
            </h3>
          </div>
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '10px'
          }}>
            {onlineUsers.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                color: '#666', 
                padding: '20px',
                fontSize: '14px'
              }}>
                {t.noUsers}
              </div>
            ) : (
              onlineUsers.map((onlineUser) => {
                const isCurrentUser = onlineUser.id === user.id;
                
                return (
                  <div
                    key={onlineUser.id}
                    onClick={() => !isCurrentUser && handleUserClick(onlineUser.id)}
                    style={{
                      padding: '12px 15px',
                      marginBottom: '8px',
                      borderRadius: '8px',
                      backgroundColor: selectedChat === onlineUser.id ? '#222' : '#1a1a1a',
                      cursor: isCurrentUser ? 'default' : 'pointer',
                      transition: 'background-color 0.2s',
                      border: selectedChat === onlineUser.id ? '1px solid #e91e63' : '1px solid transparent',
                      opacity: isCurrentUser ? 0.7 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrentUser && selectedChat !== onlineUser.id) {
                        e.target.style.backgroundColor = '#222';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrentUser && selectedChat !== onlineUser.id) {
                        e.target.style.backgroundColor = '#1a1a1a';
                      }
                    }}
                  >
                    <div style={{ 
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>{onlineUser.nickname || onlineUser.email || t.anonymous}</span>
                      {isCurrentUser && <span style={{ fontSize: '11px', color: '#e91e63' }}>({t.you})</span>}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#4caf50'
                      }}></span>
                      {isCurrentUser ? t.you : 'Online'}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GlobalChat;
